import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Mail, UserPlus, Trash2, ShieldCheck, Edit3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ConfirmModal from "../components/ConfirmModal";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [admin, setAdmin] = useState(null); // {users, pending_invitations}
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [editingUid, setEditingUid] = useState(null);
  const [editName, setEditName] = useState("");
  const [confirm, setConfirm] = useState(null);

  const loadAdmin = () => api.get("/admin/users").then((r) => setAdmin(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(console.error);
    if (user?.is_admin) loadAdmin();
  }, [user?.is_admin]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    try {
      await api.post("/admin/invitations", {
        email: inviteEmail.trim(),
        name: inviteName.trim() || null,
        note: inviteNote.trim() || null,
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteName("");
      setInviteNote("");
      loadAdmin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite");
    }
  };

  const revoke = (email) => {
    setConfirm({
      title: "Revoke invitation?",
      message: `This will cancel the invitation for ${email}. They won't be able to sign in unless re-invited.`,
      confirmLabel: "Revoke",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/invitations/${encodeURIComponent(email)}`);
          toast.success("Invitation revoked");
          loadAdmin();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not revoke");
        }
      },
    });
  };

  const removeUser = (uid, email) => {
    setConfirm({
      title: `Remove ${email}?`,
      message: "They'll lose access immediately and their personal contacts will be deleted.",
      confirmLabel: "Remove user",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${uid}`);
          toast.success(`${email} removed`);
          loadAdmin();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not remove");
        }
      },
    });
  };

  const promote = (uid, email) => {
    setConfirm({
      title: `Make ${email} an admin?`,
      message: "Admins can invite/remove people, approve stays, and edit Settings.",
      confirmLabel: "Make admin",
      onConfirm: async () => {
        try {
          await api.post(`/admin/users/${uid}/promote`);
          toast.success(`${email} is now an admin`);
          loadAdmin();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not promote");
        }
      },
    });
  };

  const demote = (uid, email) => {
    setConfirm({
      title: `Remove admin from ${email}?`,
      message: "They'll keep their access but can no longer manage Settings or approve stays.",
      confirmLabel: "Remove admin",
      onConfirm: async () => {
        try {
          await api.post(`/admin/users/${uid}/demote`);
          toast.success(`${email} is no longer admin`);
          loadAdmin();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not demote");
        }
      },
    });
  };

  const startEdit = (u) => {
    setEditingUid(u.user_id);
    setEditName(u.name || "");
  };

  const saveEdit = async (uid) => {
    if (!editName.trim()) {
      toast.error("Name can't be empty");
      return;
    }
    try {
      await api.patch(`/admin/users/${uid}`, { name: editName.trim() });
      toast.success("Saved");
      setEditingUid(null);
      loadAdmin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  if (!s) return <div className="text-sm text-[#9A9A9A]">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Ajustes</p>
        <h1 className="font-serif-villa text-4xl">Settings</h1>
        <p className="text-sm text-[#7A7A7A] mt-1">Costs are per stay (not per night). Shown to family as informational only.</p>
      </div>

      {/* Family-only message */}
      {!user?.is_admin && (
        <div className="villa-card p-5 text-sm text-[#7A7A7A]">
          Only the admin can change rates and invite people. You can still browse the calendar and request a stay.
        </div>
      )}

      <div className="villa-card p-6 sm:p-8 space-y-5">
        <Field label="Villa name">
          <input data-testid="set-villa-name" disabled={!user?.is_admin} className={inputCls} value={s.villa_name} onChange={(e) => setS({ ...s, villa_name: e.target.value })} />
        </Field>
        <Field label="Currency symbol">
          <input data-testid="set-currency" disabled={!user?.is_admin} className={inputCls + " w-24"} value={s.currency_symbol} onChange={(e) => setS({ ...s, currency_symbol: e.target.value })} />
        </Field>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Family rate (per stay)">
            <Money sym={s.currency_symbol} value={s.family_rate} disabled={!user?.is_admin} onChange={(v) => setS({ ...s, family_rate: v })} testid="set-family-rate" />
          </Field>
          <Field label="Friends rate (per stay)">
            <Money sym={s.currency_symbol} value={s.friends_rate} disabled={!user?.is_admin} onChange={(v) => setS({ ...s, friends_rate: v })} testid="set-friends-rate" />
          </Field>
          <Field label="Car add-on (per stay)">
            <Money sym={s.currency_symbol} value={s.car_rate} disabled={!user?.is_admin} onChange={(v) => setS({ ...s, car_rate: v })} testid="set-car-rate" />
          </Field>
          <Field label="Alicante fee (per leg)">
            <Money sym={s.currency_symbol} value={s.alicante_fee_per_leg ?? 35} disabled={!user?.is_admin} onChange={(v) => setS({ ...s, alicante_fee_per_leg: v })} testid="set-alc-fee" />
          </Field>
        </div>

        <div className="pt-2 border-t border-[#E8E1D5]">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Show estimated costs to guests</div>
          <p className="text-xs text-[#9A9A9A] mt-1">
            Choose which stay categories see the estimated contribution on bookings & emails.
            Untick both to hide costs entirely.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="cost-visibility-group">
            {[
              { key: "family", label: "Family stays" },
              { key: "friends", label: "Friends stays" },
            ].map(({ key, label }) => {
              const list = s.cost_visibility || [];
              const on = list.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`cost-visibility-${key}`}
                  disabled={!user?.is_admin}
                  onClick={() => {
                    const next = on ? list.filter((x) => x !== key) : [...list, key];
                    setS({ ...s, cost_visibility: next });
                  }}
                  className={`px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 transition-colors ${
                    on
                      ? "bg-[#6B705C] text-[#FDFBF7] border border-[#6B705C]"
                      : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
                  } ${!user?.is_admin ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <span className={`w-2 h-2 rounded-full ${on ? "bg-[#FDFBF7]" : "bg-[#C9C2B5]"}`} />
                  {label}
                </button>
              );
            })}
          </div>
          {(s.cost_visibility || []).length === 0 && (
            <p className="text-xs text-[#A84C30] mt-2" data-testid="cost-visibility-none-hint">
              Costs are currently hidden everywhere.
            </p>
          )}
        </div>

        {/* Dev mode — simulate emails (admin only) */}
        {user?.is_admin && (
          <div className="pt-2 border-t border-[#E8E1D5]" data-testid="dev-mode-section">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Dev mode</div>
            <p className="text-xs text-[#9A9A9A] mt-1">When on, outgoing emails are <em>simulated</em> — logged for review but never delivered. Handy for testing the booking flow without bothering the cleaner or the hire company.</p>
            <label className="mt-3 inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="set-dev-mode"
                className="w-4 h-4 accent-[#C25E3E]"
                checked={!!s.dev_mode_email_simulate}
                onChange={(e) => setS({ ...s, dev_mode_email_simulate: e.target.checked })}
              />
              <span className="text-sm text-[#2B2B2B]">Simulate emails (don't actually send)</span>
              {s.dev_mode_email_simulate && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FBE9E7] text-[#A84C30] border border-[#F2C9B6]" data-testid="dev-mode-pill">Simulating</span>
              )}
            </label>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-[#E8E1D5]">
          <Field label="Villa address (for PDF & emails)">
            <input data-testid="set-villa-address" disabled={!user?.is_admin} className={inputCls} placeholder="Street, La Manga Club, Murcia"
              value={s.villa_address || ""} onChange={(e) => setS({ ...s, villa_address: e.target.value })} />
          </Field>
          <Field label="Cleaner name">
            <input data-testid="set-cleaner-name" disabled={!user?.is_admin} className={inputCls} placeholder="e.g. María"
              value={s.cleaner_name || ""} onChange={(e) => setS({ ...s, cleaner_name: e.target.value })} />
          </Field>
          <Field label="Cleaner WhatsApp number" hint="International format, e.g. +34 612 345 678">
            <input data-testid="set-cleaner-phone" disabled={!user?.is_admin} className={inputCls} placeholder="+34..."
              value={s.cleaner_phone || ""} onChange={(e) => setS({ ...s, cleaner_phone: e.target.value })} />
          </Field>
          <Field label="Car hire company">
            <input data-testid="set-car-company" disabled={!user?.is_admin} className={inputCls} placeholder="e.g. La Manga Car Hire"
              value={s.car_hire_company || ""} onChange={(e) => setS({ ...s, car_hire_company: e.target.value })} />
          </Field>
          <Field label="Car hire email">
            <input type="email" data-testid="set-car-email" disabled={!user?.is_admin} className={inputCls} placeholder="bookings@..."
              value={s.car_hire_email || ""} onChange={(e) => setS({ ...s, car_hire_email: e.target.value })} />
          </Field>
          <Field label="Cleaner email">
            <input type="email" data-testid="set-cleaner-email" disabled={!user?.is_admin} className={inputCls} placeholder="cleaner@..."
              value={s.cleaner_email || ""} onChange={(e) => setS({ ...s, cleaner_email: e.target.value })} />
          </Field>
          <Field label="Next housekeeping reference #" hint="The ### in YYYY-### — auto-increments after each booking">
            <input type="number" data-testid="set-hk-ref" disabled={!user?.is_admin} className={inputCls} min={1}
              value={s.housekeeping_ref_next ?? 15}
              onChange={(e) => setS({ ...s, housekeeping_ref_next: parseInt(e.target.value || "1", 10) })} />
          </Field>
        </div>

        <RoomsEditor s={s} setS={setS} disabled={!user?.is_admin} />

        <div className="pt-2 border-t border-[#E8E1D5]">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">The villa car</div>
          <p className="text-xs text-[#9A9A9A] mt-1">These details are sent to the hire company in every car request — type once, never re-type.</p>
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <Field label="Make">
              <input data-testid="set-car-make" disabled={!user?.is_admin} className={inputCls} placeholder="e.g. Seat"
                value={s.car_make || ""} onChange={(e) => setS({ ...s, car_make: e.target.value })} />
            </Field>
            <Field label="Model">
              <input data-testid="set-car-model" disabled={!user?.is_admin} className={inputCls} placeholder="e.g. Ibiza"
                value={s.car_model || ""} onChange={(e) => setS({ ...s, car_model: e.target.value })} />
            </Field>
            <Field label="Colour">
              <input data-testid="set-car-colour" disabled={!user?.is_admin} className={inputCls} placeholder="e.g. White"
                value={s.car_colour || ""} onChange={(e) => setS({ ...s, car_colour: e.target.value })} />
            </Field>
            <Field label="Registration">
              <input data-testid="set-car-reg" disabled={!user?.is_admin} className={inputCls} placeholder="e.g. 7882 HYC"
                value={s.car_reg || ""} onChange={(e) => setS({ ...s, car_reg: e.target.value })} />
            </Field>
            <Field label="Account email" hint="The email registered with the car hire company">
              <input type="email" data-testid="set-car-account-email" disabled={!user?.is_admin} className={inputCls} placeholder="account@..."
                value={s.car_account_email || ""} onChange={(e) => setS({ ...s, car_account_email: e.target.value })} />
            </Field>
          </div>
        </div>

        <div className="pt-2 border-t border-[#E8E1D5]">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Airport pick-up & drop-off details</div>
          <p className="text-xs text-[#9A9A9A] mt-1">Sent to guests in the confirmation email so they know exactly where to go.</p>
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <Field label="Murcia (RMU) — pick-up">
              <textarea data-testid="set-rmu-pickup" disabled={!user?.is_admin} className={inputCls + " min-h-[90px] resize-y"} placeholder="e.g. Plane Parking desk in arrivals hall, ground floor."
                value={s.rmu_pickup_details || ""} onChange={(e) => setS({ ...s, rmu_pickup_details: e.target.value })} />
            </Field>
            <Field label="Murcia (RMU) — drop-off">
              <textarea data-testid="set-rmu-dropoff" disabled={!user?.is_admin} className={inputCls + " min-h-[90px] resize-y"} placeholder="e.g. Return to designated bay opposite departures."
                value={s.rmu_dropoff_details || ""} onChange={(e) => setS({ ...s, rmu_dropoff_details: e.target.value })} />
            </Field>
            <Field label="Alicante (ALC) — pick-up">
              <textarea data-testid="set-alc-pickup" disabled={!user?.is_admin} className={inputCls + " min-h-[90px] resize-y"} placeholder="e.g. Counter 14 in arrivals."
                value={s.alc_pickup_details || ""} onChange={(e) => setS({ ...s, alc_pickup_details: e.target.value })} />
            </Field>
            <Field label="Alicante (ALC) — drop-off">
              <textarea data-testid="set-alc-dropoff" disabled={!user?.is_admin} className={inputCls + " min-h-[90px] resize-y"} placeholder="e.g. Drop bay P3, level -1."
                value={s.alc_dropoff_details || ""} onChange={(e) => setS({ ...s, alc_dropoff_details: e.target.value })} />
            </Field>
          </div>
        </div>

        {user?.is_admin && (
          <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="villa-btn-primary px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
          </button>
        )}
      </div>

      {/* Admin: People who can sign in */}
      {user?.is_admin && (
        <div className="villa-card p-6 sm:p-8" data-testid="admin-panel">
          <div className="mb-4">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Familia · acceso</p>
            <h2 className="font-serif-villa text-2xl">Who can sign in</h2>
            <p className="text-sm text-[#7A7A7A] mt-1">Only invited Google emails can access Casa Blunt.</p>
          </div>

          <form onSubmit={invite} className="grid md:grid-cols-4 gap-3 mb-6">
            <input
              data-testid="invite-name"
              placeholder="Name"
              className={inputCls}
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
            <input
              type="email"
              required
              data-testid="invite-email"
              placeholder="name@gmail.com"
              className={inputCls}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <input
              data-testid="invite-note"
              placeholder="Optional note in the invite"
              className={inputCls}
              value={inviteNote}
              onChange={(e) => setInviteNote(e.target.value)}
            />
            <button type="submit" data-testid="send-invite-btn" className="villa-btn-primary px-5 py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-2">
              <UserPlus className="w-4 h-4" /> Invite
            </button>
          </form>

          {/* Active members */}
          <div className="space-y-2">
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">Members</h3>
            {admin?.users?.map((u) => (
              <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl bg-[#FDFBF7] border border-[#E8E1D5]" data-testid={`member-${u.user_id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#F5F0E6] border border-[#E8E1D5] flex items-center justify-center text-xs text-[#7A7A7A]">
                      {(u.name || u.email)[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    {editingUid === u.user_id ? (
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`edit-name-input-${u.user_id}`}
                          autoFocus
                          className="bg-[#FDFBF7] border border-[#E8E1D5] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E] flex-1"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveEdit(u.user_id)}
                        />
                        <button onClick={() => saveEdit(u.user_id)} data-testid={`save-name-${u.user_id}`} className="text-xs px-3 py-1.5 rounded-full bg-[#C25E3E] text-[#FDFBF7] hover:bg-[#A84C30]">Save</button>
                        <button onClick={() => setEditingUid(null)} className="text-xs px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-[#2B2B2B]">{u.name || u.email}</div>
                        <div className="text-xs text-[#7A7A7A]">{u.email}</div>
                      </>
                    )}
                  </div>
                  {u.is_admin && (
                    <span className="villa-chip villa-chip-ok inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin</span>
                  )}
                </div>
                {editingUid !== u.user_id && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={() => startEdit(u)} data-testid={`edit-${u.user_id}`} className="text-xs px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1">
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                    {u.user_id !== user?.user_id && (
                      <>
                        {u.is_admin ? (
                          <button onClick={() => demote(u.user_id, u.email)} data-testid={`demote-${u.user_id}`} className="text-xs px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Remove admin</button>
                        ) : (
                          <button onClick={() => promote(u.user_id, u.email)} data-testid={`promote-${u.user_id}`} className="text-xs px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Make admin</button>
                        )}
                        <button onClick={() => removeUser(u.user_id, u.email)} data-testid={`remove-${u.user_id}`} className="p-2 rounded-full hover:bg-[#FBE9E7] text-[#A84C30]" title="Remove user"><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pending invitations */}
          {admin?.pending_invitations?.length > 0 && (
            <div className="mt-6 space-y-2">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">Pending invitations</h3>
              {admin.pending_invitations.map((i) => (
                <div key={i.email} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-[#FBF7EF] border border-[#E8E1D5]">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#7A7A7A]" />
                    <div className="text-sm text-[#2B2B2B]">{i.email}</div>
                    <span className="text-xs text-[#9A9A9A]">invited by {i.invited_by}</span>
                  </div>
                  <button onClick={() => revoke(i.email)} data-testid={`revoke-${i.email}`} className="text-xs px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#FBE9E7] hover:text-[#A84C30]">Revoke</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="villa-card p-6 sm:p-8">
        <h3 className="font-serif-villa text-xl">Email notifications</h3>
        <p className="text-sm text-[#7A7A7A] mt-1">
          Booking and approval emails are sent via Resend. Add or update <code>RESEND_API_KEY</code>, <code>SENDER_EMAIL</code>, and <code>ADMIN_EMAIL</code> in the backend environment to control delivery.
        </p>
      </div>

      <ConfirmModal open={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">{label}</span>
      {hint && <span className="block text-[11px] text-[#9A9A9A] mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function RoomsEditor({ s, setS, disabled }) {
  const rooms = s.rooms || [];
  const addRoom = (type) => {
    const id = `rm_${Date.now().toString(36)}_${Math.floor(Math.random() * 1000)}`;
    const name = type === "bunk" ? "Bunk Bed Room" : `Bedroom ${rooms.length + 1}`;
    setS({ ...s, rooms: [...rooms, { room_id: id, name, type }] });
  };
  const updateRoom = (idx, patch) => {
    const next = rooms.slice();
    next[idx] = { ...next[idx], ...patch };
    setS({ ...s, rooms: next });
  };
  const removeRoom = (idx) => {
    setS({ ...s, rooms: rooms.filter((_, i) => i !== idx) });
  };
  const moveRoom = (idx, dir) => {
    const next = rooms.slice();
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setS({ ...s, rooms: next });
  };
  return (
    <div className="pt-2 border-t border-[#E8E1D5]" data-testid="rooms-editor">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Bedrooms</div>
          <p className="text-xs text-[#9A9A9A] mt-1">Set up the bedrooms once. When booking a stay, guests pick which rooms they need + the bed configuration for each.</p>
        </div>
        {!disabled && (
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={() => addRoom("standard")} data-testid="add-room-standard" className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">+ Bedroom</button>
            <button type="button" onClick={() => addRoom("bunk")} data-testid="add-room-bunk" className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">+ Bunk room</button>
          </div>
        )}
      </div>
      {rooms.length === 0 && <div className="mt-3 text-sm text-[#9A9A9A] italic">No rooms yet — add at least one bedroom.</div>}
      <ul className="space-y-2 mt-3">
        {rooms.map((r, i) => (
          <li key={r.room_id} className="flex items-center gap-2 p-3 rounded-xl border border-[#E8E1D5] bg-[#FBF7EF]" data-testid={`room-row-${i}`}>
            <input
              disabled={disabled}
              className={inputCls + " flex-1"}
              value={r.name}
              onChange={(e) => updateRoom(i, { name: e.target.value })}
              data-testid={`room-name-${i}`}
            />
            <span className="text-xs uppercase tracking-wider text-[#7A7A7A] w-20 text-center">{r.type === "bunk" ? "Bunk" : "Standard"}</span>
            {!disabled && (
              <>
                <button type="button" onClick={() => moveRoom(i, -1)} disabled={i === 0} className="px-2 py-1 text-xs text-[#7A7A7A] hover:text-[#2B2B2B] disabled:opacity-30">↑</button>
                <button type="button" onClick={() => moveRoom(i, 1)} disabled={i === rooms.length - 1} className="px-2 py-1 text-xs text-[#7A7A7A] hover:text-[#2B2B2B] disabled:opacity-30">↓</button>
                <button type="button" onClick={() => removeRoom(i)} data-testid={`room-remove-${i}`} className="px-2 py-1 text-xs text-[#A84C30] hover:underline">Remove</button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Money({ sym, value, onChange, testid, disabled }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[#7A7A7A]">{sym}</span>
      <input
        type="number"
        min="0"
        disabled={disabled}
        data-testid={testid}
        className={inputCls}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

const inputCls = "w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 text-[#2B2B2B] placeholder:text-[#B5B5B5] focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E] transition-all disabled:opacity-60";
